import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { supabase } from "../lib/supabase";
import PasswordStrength from "../components/PasswordStrength";

/**
 * The recovery link redirects here with tokens in the URL fragment (Supabase
 * convention). We extract them, prompt for a new password, and POST to the
 * backend which uses the admin API to persist the change.
 */
export default function ResetConfirm() {
  const nav = useNavigate();
  const [tokens, setTokens] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, "");
    if (!hash) { setError("Missing reset token. Request a new link."); return; }
    const p = new URLSearchParams(hash);
    const access_token = p.get("access_token");
    const refresh_token = p.get("refresh_token") || "";
    const type = p.get("type");
    if (!access_token || type !== "recovery") {
      setError("This isn't a valid password-reset link. Request a new one.");
      return;
    }
    setTokens({ access_token, refresh_token });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset", { ...tokens, new_password: pw });
      // Sign the user in immediately so they land on the dashboard.
      try {
        await supabase.auth.setSession({ access_token: tokens.access_token, refresh_token: tokens.refresh_token });
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.error("[ResetConfirm] setSession failed", err);
      }
      toast.success("Password updated");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  if (error) return (
    <div className="min-h-screen grid place-items-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-8">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Link expired</h1>
        <p className="mt-2 text-sm text-slate-500">{error}</p>
        <a href="/reset" className="mt-6 inline-block rounded-md px-4 py-2 text-sm font-semibold text-white" style={{ background: "var(--primary-hex)" }}>Request a new link</a>
      </div>
    </div>
  );

  if (!tokens) return <div className="min-h-screen grid place-items-center text-sm text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
        <h1 className="font-heading text-3xl font-bold tracking-tighter text-slate-900" data-testid="reset-confirm-title">Set a new password</h1>
        <p className="mt-2 text-sm text-slate-500">Choose something at least 8 characters. You'll be signed in as soon as it saves.</p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">New password</span>
            <input type="password" required value={pw} onChange={e => setPw(e.target.value)} className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus-brand" data-testid="new-password-input" />
          </label>
          <PasswordStrength password={pw} />
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Confirm password</span>
            <input type="password" required value={pw2} onChange={e => setPw2(e.target.value)} className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus-brand" data-testid="confirm-password-input" />
            {pw2 && pw !== pw2 && <span className="mt-1 block text-xs text-rose-600">Passwords don't match</span>}
          </label>
          <button
            type="submit" disabled={busy || pw.length < 8 || pw !== pw2}
            className="w-full rounded-md px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: "var(--primary-hex)" }}
            data-testid="reset-confirm-submit"
          >
            {busy ? "Saving…" : "Save & sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
